import { DSCardWrapper } from "@components/design-system/molecules";
import mixpanel from "mixpanel-browser";
import { useState } from "react";
import { useWatch } from "react-hook-form";

import NameAndSaveStep from "./NameAndSaveStep";
import PreviewStep from "./PreviewStep";
import StartingPointStep from "./StartingPointStep";
import TemplatePreferencesStep from "./TemplatePreferencesStep";
import TemplateStructureStep from "./TemplateStructureStep";
import WizardStepIndicator from "./WizardStepIndicator";
import TemplateSaveSuccessModal from "../TemplateSaveSuccessModal";

const WIZARD_STEPS = {
  STARTING_POINT: 1,
  TEMPLATE_STRUCTURE: 2,
  TEMPLATE_PREFERENCES: 3,
  PREVIEW: 4,
  NAME_AND_SAVE: 5,
};

export default function TemplateWizard({
  control,
  loadingExtraction,
  loadingPreview,
  previewContent,
  handleFileUpload,
  handlePreview,
  handleClearSampleTranscript,
  previewSource,
  setPreviewSource,
  selectedNote,
  onSelectNote,
  onSave,
  saving,
  clearPreview,
  showSuccessModal,
  onCloseSuccessModal,
  onShareTemplate,
  onCreateTemplate,
}) {
  const [selectedMethod, setSelectedMethod] = useState(null);
  const templateUuid = useWatch({ control, name: "uuid" });
  const [currentStep, setCurrentStep] = useState(
    templateUuid
      ? WIZARD_STEPS.TEMPLATE_STRUCTURE
      : WIZARD_STEPS.STARTING_POINT,
  );
  const formData = useWatch({ control });

  const trackStepTransition = (from, to) => {
    mixpanel.track("Wizard Step Transition", {
      templateUuid,
      fromStep: from,
      toStep: to,
    });
  };

  const goToStep = (step) => {
    trackStepTransition(currentStep, step);
    setCurrentStep(step);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="my-lg">
      <DSCardWrapper
        padding="p-lg"
        variant="ghost"
        data-testid="template-wizard"
      >
        {selectedMethod !== "quill" && (
          <WizardStepIndicator currentStep={currentStep} />
        )}

        <div className="flex-1 mt-lg">
          {currentStep === WIZARD_STEPS.STARTING_POINT && (
            <StartingPointStep
              control={control}
              onNext={async () => {
                if (onCreateTemplate && !templateUuid) {
                  const newUuid = await onCreateTemplate();
                  if (!newUuid) return;
                }
                goToStep(WIZARD_STEPS.TEMPLATE_STRUCTURE);
              }}
              onFileUpload={handleFileUpload}
              loadingExtraction={loadingExtraction}
              selectedMethod={selectedMethod}
              onMethodChange={setSelectedMethod}
            />
          )}

          {currentStep === WIZARD_STEPS.TEMPLATE_STRUCTURE && (
            <TemplateStructureStep
              control={control}
              onNext={() =>
                goToStep(WIZARD_STEPS.TEMPLATE_PREFERENCES)
              }
              onBack={
                selectedMethod
                  ? () => goToStep(WIZARD_STEPS.STARTING_POINT)
                  : undefined
              }
              templateUuid={templateUuid}
            />
          )}

          {currentStep === WIZARD_STEPS.TEMPLATE_PREFERENCES && (
            <TemplatePreferencesStep
              control={control}
              onNext={() => goToStep(WIZARD_STEPS.PREVIEW)}
              onBack={() => goToStep(WIZARD_STEPS.TEMPLATE_STRUCTURE)}
              templateUuid={templateUuid}
            />
          )}

          {currentStep === WIZARD_STEPS.PREVIEW && (
            <PreviewStep
              control={control}
              onBack={() =>
                goToStep(WIZARD_STEPS.TEMPLATE_PREFERENCES)
              }
              onKeepEditing={() => {
                clearPreview();
                goToStep(WIZARD_STEPS.TEMPLATE_STRUCTURE);
              }}
              onLooksGood={() => goToStep(WIZARD_STEPS.NAME_AND_SAVE)}
              onSkip={() => goToStep(WIZARD_STEPS.NAME_AND_SAVE)}
              loadingPreview={loadingPreview}
              previewContent={previewContent}
              handlePreview={handlePreview}
              handleClearSampleTranscript={
                handleClearSampleTranscript
              }
              previewSource={previewSource}
              setPreviewSource={setPreviewSource}
              selectedNote={selectedNote}
              onSelectNote={onSelectNote}
            />
          )}

          {currentStep === WIZARD_STEPS.NAME_AND_SAVE && (
            <NameAndSaveStep
              control={control}
              onBack={() => goToStep(WIZARD_STEPS.PREVIEW)}
              onSave={onSave}
              saving={saving}
            />
          )}
        </div>
      </DSCardWrapper>

      <TemplateSaveSuccessModal
        isOpen={!!showSuccessModal}
        onClose={onCloseSuccessModal}
        onShareTemplate={onShareTemplate}
        templateUuid={templateUuid}
        templateName={formData?.name}
        templateType={formData?.templateType}
        creationMethod="wizard"
        showFeedback
      />
    </div>
  );
}
